import BlogSection from "@/components/BlogSection";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Phone, ShieldCheck, Clock, Users, MapPin,
  Network, ServerCog, Layers, Boxes, Landmark, HeartPulse, Factory,
  ShoppingBag, GraduationCap, Truck, Radio, Building2, Quote, Check,
} from "lucide-react";
import { SERVICES, INDUSTRIES, TESTIMONIALS, STATS, WHY_CHOOSE, COMPANY } from "@/lib/site-data";
import SectionHeader from "@/components/SectionHeader";

const ICONS = {
  Network, ServerCog, Layers, Boxes,
  Clock, MapPin, Users, ShieldCheck,
  Landmark, HeartPulse, Factory, ShoppingBag, GraduationCap, Truck, Radio, Building2,
};

function Icon({ name, className }) {
  const C = ICONS[name];
  return C ? <C className={className} /> : null;
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute inset-0 grid-bg opacity-100 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-blue-50 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 pulse-dot" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                </span>
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">
                  24/7 NOC · Pan-India Support
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-slate-900 leading-[1.02]">
                Reliable <span className="text-red-600">24/7</span> IT Support<br />
                Across <span className="relative inline-block">
                  India
                  <span className="absolute -bottom-2 left-0 right-0 h-2 bg-blue-200/70 -z-0" />
                </span>
              </h1>
              <p className="mt-7 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
                Empowering your business with expert IT solutions and skilled technical
                manpower — engineered to keep your infrastructure running at peak performance.
              </p>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-quote-button"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-4 rounded-md font-semibold text-base transition-all duration-300 hover:shadow-xl hover:shadow-red-600/20 hover:-translate-y-0.5"
                >
                  Get a Quote <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-contact-button"
                  className="inline-flex items-center gap-2 border-2 border-slate-200 hover:border-blue-900 text-slate-900 hover:text-blue-900 px-7 py-4 rounded-md font-semibold text-base transition-all"
                >
                  Contact Us
                </Link>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  data-testid="hero-call-link"
                  className="inline-flex items-center gap-2 text-slate-900 hover:text-blue-900 px-2 py-4 font-semibold"
                >
                  <Phone className="h-4 w-4" /> {COMPANY.phone}
                </a>
              </div>

              {/* Stats */}
              <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-6 border-t border-slate-200 pt-8" data-testid="hero-stats">
                {STATS.map((s, i) => (
                  <div key={s.label} className={`fade-up fade-up-${i + 1}`}>
                    <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
                      {s.value}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 fade-up fade-up-2">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
                <img
                  src="https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Data center infrastructure"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-950/70 via-blue-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                  <p className="text-xs uppercase tracking-[0.25em] text-blue-200 mb-2">
                    Mission Control
                  </p>
                  <p className="font-display text-2xl font-bold leading-tight">
                    Always-on engineering for always-on infrastructure.
                  </p>
                </div>
                {/* Floating SLA badge */}
                <div className="absolute top-6 right-6 backdrop-blur-md bg-white/90 border border-white/40 rounded-lg px-4 py-3 shadow-lg">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">SLA Uptime</div>
                  <div className="font-display text-2xl font-black text-slate-900">99.9%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <SectionHeader
              eyebrow="What we do"
              title="End-to-end IT services, on-ground & on-call."
              description="From router to rack to cloud — we deliver the engineering muscle that keeps your business connected."
              testid="services-section-header"
            />
            <Link
              to="/services"
              data-testid="services-explore-link"
              className="inline-flex items-center gap-2 text-blue-900 font-semibold hover:gap-3 transition-all"
            >
              Explore all services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="services-grid">
            {SERVICES.map((s, idx) => (
              <Link
                key={s.slug}
                to="/services"
                data-testid={`service-card-${s.slug}`}
                className="group bg-white border border-slate-200 rounded-xl p-7 hover:border-blue-300 hover:shadow-[0_8px_30px_rgb(15,23,42,0.06)] hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
              >
                <div className="h-12 w-12 rounded-lg bg-blue-50 text-blue-900 flex items-center justify-center mb-5 group-hover:bg-blue-900 group-hover:text-white transition-colors">
                  <Icon name={s.icon} className="h-6 w-6" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  0{idx + 1} / Service
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-3">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{s.short}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-900 group-hover:gap-3 transition-all">
                  Learn more <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative bg-slate-950 grid-bg-dark text-white py-20 lg:py-32 overflow-hidden" data-testid="why-choose-section">
        <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-blue-900/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 mb-4">
                Why R-Tech
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] mb-6">
                Engineered for uptime. <br />
                Trusted by enterprises.
              </h2>
              <p className="text-slate-300 leading-relaxed text-base sm:text-lg max-w-md">
                We combine deep technical expertise with disciplined service delivery —
                because every minute of downtime costs your business.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/about" data-testid="why-about-link" className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-blue-50 px-6 py-3 rounded-md font-semibold transition-all">
                  About R-Tech <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" data-testid="why-contact-link" className="inline-flex items-center gap-2 border-2 border-slate-700 hover:border-white text-white px-6 py-3 rounded-md font-semibold transition-all">
                  Talk to engineers
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {WHY_CHOOSE.map((w, i) => (
                <div
                  key={w.title}
                  data-testid={`why-card-${i}`}
                  className="bg-white/5 border border-white/10 backdrop-blur-md rounded-xl p-7 hover:bg-white/10 hover:border-white/20 transition-all"
                >
                  <div className="h-11 w-11 rounded-lg bg-red-600/15 text-red-400 flex items-center justify-center mb-4">
                    <Icon name={w.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-bold mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-white py-20 lg:py-28" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            eyebrow="Industries we serve"
            title="From banks to factories — we keep them online."
            description="Tailored IT engineering for the unique demands of every sector."
            align="center"
            testid="industries-header"
          />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {INDUSTRIES.map((ind) => (
              <div
                key={ind.name}
                data-testid={`industry-${ind.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="group flex items-center gap-3 border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:bg-blue-50/40 transition-all"
              >
                <div className="h-10 w-10 rounded-lg bg-slate-100 group-hover:bg-blue-900 group-hover:text-white text-slate-700 flex items-center justify-center transition-colors">
                  <Icon name={ind.icon} className="h-5 w-5" />
                </div>
                <span className="text-sm font-semibold text-slate-800">{ind.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            eyebrow="Trusted by IT leaders"
            title="What our clients say."
            testid="testimonials-header"
          />
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white border border-slate-200 rounded-xl p-8 flex flex-col"
              >
                <Quote className="h-8 w-8 text-red-600 mb-5" />
                <p className="text-base text-slate-700 leading-relaxed flex-1">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="font-display font-bold text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section className="bg-blue-900 text-white" data-testid="promise-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid sm:grid-cols-3 gap-8">
          {[
            "Certified L2/L3 Engineers",
            "Pan-India On-ground Manpower",
            "Transparent SLA-driven Delivery",
          ].map((p) => (
            <div key={p} className="flex items-center gap-3" data-testid={`promise-${p.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5" />
              </div>
              <p className="font-display text-lg font-semibold">{p}</p>
            </div>
          ))}
        </div>
      </section>
      <BlogSection />
    </div>
  );
}
